import React, { useCallback, useEffect } from "react";
import { Typography } from "@mui/material";
import { useMutation, useQuery } from "@tanstack/react-query";
import DoneIcon from "@mui/icons-material/Done";
import Button from "@mui/material/Button";
import { useTranslate } from "../../../hooks/useTranslate";
import Preloader from "../../../components/Preloader";
import axiosManagerRegister from "../../../services/axiosManagerRegister";
import { useToast } from "../../../context/toast";

export const YearGroupSelect = (props) => {
    const {
        registrationId,
        studentName, childId,
        divisionSelected, yearGroupSelected, onYearGroupChange, onPrev, onNext,
    } = props;
    const { t } = useTranslate();
    const toast = useToast();

    const {
        isFetching: isLoadingYearGroups, data: yearGroups,
    } = useQuery({
        queryKey: [`add-student/${divisionSelected}/yearGroupStructure`],
        queryFn: () => axiosManagerRegister.get$(`/api/api/division/${divisionSelected}/yearGroupStructure`).then((response) => response?.data?.sets),
        retry: 0,
        enabled: !!divisionSelected,
    });

    const {
        isFetching: isLoadingRecommended, data: recommendedYearGroup,
    } = useQuery({
        queryKey: [`add-student/${registrationId}/child/${childId}/recommended-year-group`],
        queryFn: () => axiosManagerRegister.get$(`/api/api/registration/${registrationId}/child/${childId}/recommended-year-group`).then((response) => response?.data),
        retry: 0,
        enabled: !!registrationId && !!childId,
    });

    const {
        mutateAsync: updateYearGroup, isPending: isUpdatingYearGroup,
    } = useMutation({
        mutationFn: (requestData) => axiosManagerRegister.post$(`/api/api/registration/${registrationId}/child/year-groups`, requestData),
        onError: () => {
            toast.error(t("common.errorMessage"));
        },
    });

    useEffect(() => {
        if (!yearGroupSelected?.yearGroupId && recommendedYearGroup) {
            onYearGroupChange(recommendedYearGroup);
        }
    }, [yearGroupSelected, recommendedYearGroup]);
    useEffect(() => {
        if (yearGroupSelected && !yearGroupSelected.name && yearGroups?.length) {
            const fullYearGroup = yearGroups.find((yearGroup) => yearGroup.yearGroupId === yearGroupSelected.yearGroupId);
            onYearGroupChange(fullYearGroup);
        }
    }, [yearGroupSelected, yearGroups]);

    const onNextClick = async () => {
        try {
            await updateYearGroup([{ onlineRegistrationChildId: childId, yearGroupId: yearGroupSelected?.yearGroupId }]);

            onNext?.();
        } catch {
            // Error is in Mutation
        }
    };

    return (
        <div className="details-form">
            <Typography variant="h1">
                {t("addStudent.selectYearGroup")}
            </Typography>
            {recommendedYearGroup && (
                <Typography variant="body1" component="div">
                    {t("addStudent.selectYearGroupSub", { name: studentName, yearGroup: recommendedYearGroup?.name })}
                </Typography>
            )}
            <Preloader isLoading={isLoadingYearGroups} size={40}>
                <Preloader isLoading={isLoadingRecommended} size={40} showInitiallyOverflow overflow>
                    <div className="flex flex-wrap align-stretch gap-4 mt-4">
                        {(yearGroups || []).map((yearGroup, idx) => (
                            <div key={yearGroup.name} className="w-full mt-4">
                                <Typography variant="body1" component="div" className="text-primary">
                                    <Typography variant="h6" component="span" className="text-primary mr-2">{yearGroup.name}</Typography>
                                    {t("addStudent.yearsOld", { years: `${yearGroup.ageFrom}-${yearGroup.ageTo}` })}
                                    {` | ${t("addStudent.keyStage")} ${yearGroup.keyStage}`}
                                </Typography>
                                <div className="flex flex-wrap align-stretch gap-8 mt-4">
                                    {yearGroup.yearGroups.map((year) => (
                                        <button
                                            key={year.yearGroupId}
                                            type="button"
                                            className={`add-student-selection-option ${year.yearGroupId === yearGroupSelected?.yearGroupId ? "selected" : ""}`}
                                            onClick={() => onYearGroupChange(year)}
                                        >
                                            <span className="add-student-selection-option__icon">
                                                {year.yearGroupId === yearGroupSelected?.yearGroupId && <DoneIcon />}
                                            </span>
                                            <span
                                                className="add-student-selection-option__primary-text"
                                            >
                                                {year.name}
                                            </span>
                                        </button>
                                    ))}
                                </div>
                                {idx !== yearGroups.length - 1 && <div className="divider mt-4" />}
                            </div>
                        ))}
                    </div>
                </Preloader>
            </Preloader>
            <div className="add-student__form-narrow flex align-center gap-8 mt-8">
                <Button
                    variant="outlined"
                    className="add-student-form__button"
                    onClick={onPrev}
                    disabled={isLoadingYearGroups || isUpdatingYearGroup}
                >
                    {t("button.back")}
                </Button>
                <Button
                    variant="contained"
                    className="button-accent add-student-form__button"
                    disabled={isLoadingYearGroups || !divisionSelected}
                    loading={isUpdatingYearGroup}
                    onClick={onNextClick}
                >
                    {t("button.next")}
                </Button>
            </div>
        </div>
    );
};
